import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Breadcrumb } from "../Breadcrumb";
import API from "../api/api01";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";


interface Client {
  id: string;
  profile_id: string;
  name: string;
  tenant_id: string;
}

import { useNavigate } from "react-router-dom";
import { PATHS } from "../config/paths";
import { isValidEmail, isValidPhone, filterNumeric, isValidPassword } from "../utils/validation";

interface AddCustomerProps {}

export default function AddCustomer({}: AddCustomerProps) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    client: "",
    name: "",
    email: "",
    phone: "",
    address: "",
    designation: "",
    loginId: "",
    password: "",
    group_id: "",
  });

  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      
      // If user is a Client, we can pre-set the client ID from their own tenant_id
      if (parsedUser.role?.toLowerCase() === "client" && parsedUser.tenant_id) {
        setForm((prev) => ({ ...prev, client: parsedUser.tenant_id }));
      }
    }
    fetchClients();
  }, []);

  // Fetch groups whenever the selected client changes (for Superadmin context)
  useEffect(() => {
    fetchGroups(form.client);
  }, [form.client]);

  const fetchGroups = async (clientId?: string) => {
    try {
      setLoadingGroups(true);
      const url = clientId ? `/superadmin/groups?client_id=${clientId}` : "/superadmin/groups";
      const { data } = await API.get(url);
      setGroups(data);
    } catch (err) {
      toast.error("Failed to fetch groups");
    } finally {
      setLoadingGroups(false);
    }
  };

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const { data } = await API.get("/superadmin/clients");
      setClients(data);

      // Auto-select if Client role
      const savedUser = localStorage.getItem("user");
      if (savedUser) {
        const parsedUser = JSON.parse(savedUser);
        const userRole = (parsedUser.role || "").toLowerCase();
        
        if (userRole === "client") {
          // Find the client record where tenant_id matches user's tenant_id or profile_id matches user's id
          const myClient = data.find((c: any) => 
            (c.tenant_id && c.tenant_id === parsedUser.tenant_id) || 
            (c.profile_id && c.profile_id === parsedUser.id)
          );
          
          if (myClient) {
            setForm((prev) => ({ ...prev, client: myClient.tenant_id || myClient.id }));
          } else if (data.length === 1) {
            // Fallback: If only one client is returned and user is Client, it's definitely them
            setForm((prev) => ({ ...prev, client: data[0].tenant_id || data[0].id }));
          }
        }
      }
    } catch (err) {
      toast.error("Failed to fetch clients");
    } finally {
      setLoadingClients(false);
    }
  };

  const handleChange = (e: any) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    
    let currentClientId = form.client;
    const isClientRole = user?.role?.toLowerCase() === "client";

    // Fallback if auto-selection failed but we have it in user context
    if (!currentClientId && isClientRole && user?.tenant_id) {
      currentClientId = user.tenant_id;
    }

    // Only require client attribution if NOT a client role (i.e. Superadmin must select)
    if (!isClientRole && !currentClientId) {
      newErrors.client = "Client attribution is required";
    }
    if (!form.group_id) newErrors.group_id = "Permission group is required";
    if (!form.name.trim()) newErrors.name = "Customer name is required";
    
    if (!form.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!isValidEmail(form.email)) {
      newErrors.email = "Please enter a valid business email address";
    }

    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!isValidPhone(form.phone)) {
      newErrors.phone = "Exactly 10 digits required";
    }

    if (!form.address.trim()) newErrors.address = "Address is required";
    if (!form.designation.trim()) newErrors.designation = "Designation is required";
    if (!form.loginId.trim()) newErrors.loginId = "Login ID is required";

    if (!form.password) {
      newErrors.password = "Password is required";
    } else if (form.password.length < 8) {
      newErrors.password = "Minimum 8 characters required";
    } else if (!isValidPassword(form.password)) {
      newErrors.password = "Must include uppercase, lowercase, and a number";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fix the errors in the form");
      return;
    }

    setErrors({});

    try {
      setSubmitting(true);

      const payload = {
        client_id: currentClientId,
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        designation: form.designation,
        login_id: form.loginId,
        password: form.password,
        group_id: form.group_id || null
      };

      await API.post("/superadmin/customers", payload);

      toast.success("Customer added successfully");
      navigate(PATHS.CUSTOMER_MANAGEMENT);
    } catch (error) {
      toast.error("Failed to add customer");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6 bg-background min-h-screen">

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">Add New Customer</h1>


        <Breadcrumb
          items={[
            { label: "Manage Customer" },
            { label: "Add Customer" },
          ]}
          className="mt-2"
        />
      </div>


      {/* Main Card */}
      <Card className="bg-card border-slate-200 dark:border-slate-800 shadow-sm rounded-lg">
        <CardHeader className="border-b border-slate-100 dark:border-slate-800/60 pb-4">
          <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Customer Information</CardTitle>
          <p className="text-[13px] font-medium text-slate-500 dark:text-slate-400 mt-1">
            Fill in the details below to create a new customer account.
          </p>
        </CardHeader>



        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Client */}
            <div className="space-y-2">
              <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Client<span className="text-red-500">*</span></Label>

              {user?.role?.toLowerCase() === "client" ? (
                <Input
                  value={
                    user?.username === "lmsdemo" ? "lms demo" :
                      (user?.full_name || user?.name || user?.username || "Assigning Client...")
                  }
                  readOnly
                  className="w-full bg-background border-border cursor-not-allowed font-semibold text-slate-700 dark:text-slate-300"
                />
              ) : (
                <Select
                  value={form.client}
                  onValueChange={(val) => setForm({ ...form, client: val })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={loadingClients ? "Loading clients..." : "Select a Client"} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.tenant_id || client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {errors.client && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.client}</p>}
            </div>


            {/* Customer Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Customer Name<span className="text-red-500">*</span> </Label>

                <Input
                  name="name"
                  placeholder="Enter customer name"
                  value={form.name}
                  onChange={handleChange}
                />
                {errors.name && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Email<span className="text-red-500">*</span></Label>

                <Input
                  name="email"
                  placeholder="Enter email"
                  value={form.email}
                  onChange={handleChange}
                />
                {errors.email && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Phone Number<span className="text-red-500">*</span></Label>

                <Input
                  name="phone"
                  placeholder="Enter phone number"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: filterNumeric(e.target.value) })}
                  className="border-slate-200 dark:border-slate-800"
                />
                {errors.phone && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.phone}</p>}
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Designation<span className="text-red-500">*</span></Label>

                <Input
                  name="designation"
                  placeholder="Enter designation"
                  value={form.designation}
                  onChange={handleChange}
                />
                {errors.designation && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.designation}</p>}
              </div>

            </div>

            <div className="space-y-2">
              <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Address<span className="text-red-500">*</span></Label>

              <Input
                name="address"
                placeholder="Enter address"
                value={form.address}
                onChange={handleChange}
              />
              {errors.address && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.address}</p>}
            </div>

            <hr className="border-muted" />

            {/* Login Credentials */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground/70 mb-4">Login Credentials</h3>


              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Login ID<span className="text-red-500">*</span></Label>

                  <Input
                    name="loginId"
                    placeholder="Enter login ID"
                    value={form.loginId}
                    onChange={handleChange}
                  />
                  {errors.loginId && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.loginId}</p>}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Password<span className="text-red-500">*</span></Label>

                  <Input
                    type="password"
                    name="password"
                    placeholder="Enter password"
                    value={form.password}
                    onChange={handleChange}
                  />
                  {errors.password && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.password}</p>}
                </div>

              </div>
            </div>

            <div className="pt-6 border-t border-slate-100 dark:border-slate-800/60 mt-6">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">
                  Assign Group (Permissions)<span className="text-red-500">*</span>
                </Label>
                <Select
                  value={form.group_id}
                  onValueChange={(val) => setForm({ ...form, group_id: val })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={loadingGroups ? "Loading groups..." : "Select a Group"} />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.group_id && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.group_id}</p>}
                <p className="text-[11px] font-medium text-muted-foreground mt-1">
                  The customer will inherit permissions from this group.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 dark:border-slate-800/60 pb-6">
              <Button 
                variant="outline" 
                type="button" 
                className="rounded-lg border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800/50 text-xs font-bold uppercase tracking-widest h-10 px-8 transition-all shadow-sm active:scale-95"
                onClick={() => navigate(PATHS.CUSTOMER_MANAGEMENT)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white min-w-[160px] h-10 rounded-lg shadow-sm transition-all active:scale-95">
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4 text-white" />}
                <span className="text-[13px] font-bold uppercase tracking-widest">Add Customer</span>
              </Button>
            </div>



          </form>
        </CardContent>
      </Card>
    </div>
  );
}